import { Trans, useLingui } from "@lingui/react/macro";
import { patientKeys, updateName } from "@/api/Patients";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { FilledButton } from "@medoma/components/Button/Button";
import Form from "@/components/Form/Form";
import { Loading } from "@medoma/components/Loading/Loading";
import { deducedError } from "@/Utils/ErrorUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import InputField from "@medoma/components/InputField/InputField";
import {
  getPatientFamilyName,
  getPatientGivenName,
  type IGivenAndFamilyNameInput,
  type IPatientName,
} from "@medoma/models/patients";

export const EditName = ({
  currentName,
  onSuccess,
  patientId,
}: {
  currentName: IPatientName;
  onSuccess: () => void;
  patientId: string;
}) => {
  const { t } = useLingui();
  const {
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
    register,
    reset,
    setError,
  } = useForm({
    defaultValues: {
      givenName: getPatientGivenName(currentName),
      familyName: getPatientFamilyName(currentName),
    },
  });

  const queryClient = useQueryClient();
  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: (name: IGivenAndFamilyNameInput) => updateName(patientId, name),
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      // Reset `isDirty` to support only showing server error when the form is not changed.
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: patientKeys.detail(patientId),
      });
      onSuccess();
    },
  });

  return (
    <Form
      onSubmit={handleSubmit((formData) => {
        mutate({
          givenName: formData.givenName,
          familyName: formData.familyName,
        });
      })}
    >
      {isPending || isSuccess ? (
        <Loading message={t`Ändrar namn`} />
      ) : (
        <>
          {errors.root?.server?.message && !isDirty ? (
            <ErrorMessage message={errors.root.server.message} />
          ) : undefined}
          <Form.Row type="flex">
            <InputField
              label={t`Förnamn`}
              showOptionalLabel={false}
              errorMessage={errors.givenName?.message}
              {...register("givenName", {
                required: {
                  value: true,
                  message: t`Ange förnamn`,
                },
              })}
              outerWidth="fit"
            />
            <InputField
              label={t`Efternamn`}
              showOptionalLabel={false}
              errorMessage={errors.familyName?.message}
              {...register("familyName", {
                required: {
                  value: true,
                  message: t`Ange efternamn`,
                },
              })}
              outerWidth="fit"
            />
          </Form.Row>
          <FilledButton type="submit">
            <Trans>Spara ändringar</Trans>
          </FilledButton>
        </>
      )}
    </Form>
  );
};
